import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const Step6Exclusions = ({ data, onNext, onPrevious, onSaveAndExit, saving }) => {
  const [formData, setFormData] = useState({
    exclusions: data.exclusions || []
  });

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const selectClass = "bg-slate-800 border-slate-700 text-white w-full p-2 rounded-md focus:border-amber-500";
  const labelClass = "text-slate-300 font-medium text-sm";

  const relationshipOptions = [
    'Spouse',
    'Son',
    'Daughter',
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandson',
    'Granddaughter',
    'Nephew',
    'Niece',
    'Uncle',
    'Aunt',
    'Cousin',
    'Partner',
    'Friend',
    'Other'
  ];

  const addExclusion = () => {
    setFormData(prev => ({
      ...prev,
      exclusions: [...prev.exclusions, { name: '', relationship: '', reason: '' }]
    }));
  };

  const updateExclusion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      exclusions: prev.exclusions.map((exc, i) => 
        i === index ? { ...exc, [field]: value } : exc
      )
    }));
  };

  const removeExclusion = (index) => {
    setFormData(prev => ({
      ...prev,
      exclusions: prev.exclusions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step6-form">
      <div className="space-y-4">
        {/* Warning Box */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-300">
            <p className="font-medium mb-1">Important Note</p>
            <p className="text-amber-300/80">
              Excluding someone from your Will can have legal implications. Make sure you understand the 
              consequences and consider seeking legal advice if needed.
            </p>
          </div>
        </div>

        {/* Exclusions List */}
        {formData.exclusions.map((exclusion, index) => (
          <div key={index} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-white font-medium">Excluded Person {index + 1}</h4>
              <button 
                type="button" 
                onClick={() => removeExclusion(index)} 
                className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
              >
                <Trash2 size={16} /> Remove
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Full Name *</Label>
                <Input
                  type="text"
                  value={exclusion.name}
                  onChange={(e) => updateExclusion(index, 'name', e.target.value)}
                  required
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Relationship *</Label>
                <select
                  value={exclusion.relationship}
                  onChange={(e) => updateExclusion(index, 'relationship', e.target.value)}
                  required
                  className={selectClass}
                >
                  <option value="">Select</option>
                  {relationshipOptions.map(rel => (
                    <option key={rel} value={rel}>{rel}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}

        <Button 
          type="button" 
          onClick={addExclusion} 
          variant="outline" 
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <Plus size={16} className="mr-2" /> Add Exclusion
        </Button>

        {formData.exclusions.length === 0 && (
          <div className="bg-slate-800/30 p-6 rounded-lg text-center">
            <p className="text-slate-400">
              No exclusions added. You can skip this step if you don't want to exclude anyone.
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button type="button" onClick={onPrevious} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button type="button" onClick={() => onSaveAndExit(formData)} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Residual Assets <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step6Exclusions;
